import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getShopProduct } from "../../../network/service/ShopService";

/**
 * Product screen data (packs, nutrition, benefits, delivery, recipes). The
 * detail and nutrition screens share it, so it is cached for a few minutes:
 * opening "Nutrition information" does not fetch again.
 */
const cache = new Map();
const TTL_MS = 5 * 60 * 1000;

let location = null;
let locationAsked = false;

/** Customer location for the distance to the store; asked once, optional. */
function askLocation() {
  if (locationAsked || !navigator.geolocation) return Promise.resolve(location);
  locationAsked = true;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        location = { lat: Number(coords.latitude.toFixed(5)), lng: Number(coords.longitude.toFixed(5)) };
        resolve(location);
      },
      () => resolve(null),
      { timeout: 5000, maximumAge: 10 * 60 * 1000 },
    );
  });
}

export function useShopProduct(storeId, productId) {
  const key = `${storeId}:${productId}`;
  const cached = cache.get(key);
  const fresh = cached && Date.now() - cached.at < TTL_MS ? cached.data : null;

  const [state, setState] = useState({ data: fresh, status: fresh ? "ready" : "loading", error: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!storeId || !productId) return undefined;
    const hit = cache.get(key);
    if (attempt === 0 && hit && Date.now() - hit.at < TTL_MS) {
      setState({ data: hit.data, status: "ready", error: null });
      return undefined;
    }

    const abort = new AbortController();
    setState((s) => ({ ...s, status: "loading", error: null }));
    askLocation()
      .then((coords) => getShopProduct(storeId, productId, coords ?? undefined, abort.signal))
      .then((data) => {
        cache.set(key, { data, at: Date.now() });
        setState({ data, status: "ready", error: null });
      })
      .catch((error) => {
        if (!axios.isCancel(error)) setState({ data: null, status: "error", error });
      });
    return () => abort.abort();
  }, [key, storeId, productId, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}
